import Image from 'next/image';
import type React from 'react';
import type { SVGProps } from 'react';
import { BoltIcon } from '../icons/BoltIcon';
import Case from '../icons/Case';
import { ChevronRightIcon } from '../icons/ChevronRightIcon';
import ColorBottle from '../icons/ColorBottle';
import ColorBulb from '../icons/ColorBulb';
import ColorHashtag from '../icons/ColorHashtag';
import ColorLetsgo from '../icons/ColorLetsgo';
import ColorLoading from '../icons/ColorLoading';
import ColorNice from '../icons/ColorNice';
import ColorPin from '../icons/ColorPin';
import ColorProfileDemo from '../icons/ColorProfileDemo';
import ColorProfileEmpty from '../icons/ColorProfileEmpty';
import ColorSubmissionFail from '../icons/ColorSubmissionFail';
import ColorSubmissionSuccess from '../icons/ColorSubmissionSuccess';
import ColorTicket from '../icons/ColorTicket';
import ColorTicketDaily from '../icons/ColorTicketDaily';
import ColorYes from '../icons/ColorYes';
import Fire from '../icons/Fire';
import { HeartIcon } from '../icons/HeartIcon';
import { NewIcon } from '../icons/NewIcon';
import { PopularIcon } from '../icons/PopularIcon';
import { TicketIcon } from '../icons/TicketIcon';
import User from '../icons/User';

const iconMap = {
  bolt: BoltIcon,
  'chevron-right': ChevronRightIcon,
  heart: HeartIcon,
  new: NewIcon,
  popular: PopularIcon,
  ticket: TicketIcon,
  'color-ticket-daily': ColorTicketDaily,
  'color-ticket': ColorTicket,
  'color-bulb': ColorBulb,
  'color-hashtag': ColorHashtag,
  'color-bottle': ColorBottle,
  'color-yes': ColorYes,
  'color-pin': ColorPin,
  'color-loading': ColorLoading,
  'color-nice': ColorNice,
  'color-letsgo': ColorLetsgo,
  'color-profile-empty': ColorProfileEmpty,
  'color-profile-demo': ColorProfileDemo,
  fire: Fire,
  user: User,
  case: Case,
  'color-submission-success': ColorSubmissionSuccess,
  'color-submission-fail': ColorSubmissionFail,
  discord: 'social/discord',
  'external-link': 'external-link',
  'popow-logo': 'popow/white',
  'popow-logo-green': 'popow/green',
  google: 'social/google',
  tiktok: 'social/tiktok',
  'welcome-good': 'welcome-good',
  'heart-empty-state': 'heart-empty-state',
  empty: 'empty',
  'loading-black': 'loading-black.gif',
  'new-reward-policy': 'new-reward-policy',
  'calendar-check': 'calendar-check',
  'mail-inbox-icon': 'inbox',
  'mission-dot': 'mission-dot.gif',
  'popow-flame-1': 'popow-flame-1',
  'popow-flame-2': 'popow-flame-2',
  'popow-flame-3': 'popow-flame-3',
  'icon-map': 'map',
  'lamp-icon': 'lamp-icon.png',
  'tv-proposal': 'tv-proposal.png',
  'menu-book': 'book',
  'starter-mission': 'starter-mission',
  'connect-tiktok': 'ConnectTiktok.png',
  'warning-error': 'warning-filled',
} as const;

export type IconName = keyof typeof iconMap;

export interface IconProps extends SVGProps<SVGSVGElement> {
  name: IconName;
  width?: number;
  height?: number;
  alt?: string;
  style?: React.CSSProperties;
}

const Icon: React.FC<IconProps> = ({
  name,
  width = 24,
  height = 24,
  alt,
  className,
  style,
  ...restProps
}) => {
  const iconValue = iconMap[name];

  if (!iconValue) {
    console.warn(`Icon with name "${name}" not found.`);
    return null;
  }

  // 파일명인 경우 Image 컴포넌트로 렌더링
  if (typeof iconValue === 'string') {
    // empty 아이콘은 popow-asset/illustrations에서 로드
    if (iconValue === 'empty') {
      return (
        <Image
          src="/assets/illustrations/empty.png"
          alt={alt || name}
          width={width}
          height={height}
          className={className}
          style={style}
        />
      );
    }

    // welcome-good는 illustrations 폴더에 있음
    if (iconValue === 'welcome-good') {
      return (
        <Image
          src="/assets/illustrations/welcome-good.svg"
          alt={alt || name}
          width={width}
          height={height}
          className={className}
          style={style}
        />
      );
    }

    // popow-flame 시리즈는 illustrations 폴더에 있음
    if (iconValue.startsWith('popow-flame')) {
      const hasExtension = iconValue.includes('.');
      const imagePath = hasExtension
        ? `/assets/illustrations/${iconValue}`
        : `/assets/illustrations/${iconValue}.svg`;

      return (
        <Image
          src={imagePath}
          alt={alt || name}
          width={width}
          height={height}
          className={className}
          style={style}
        />
      );
    }

    // popow 로고는 logos/popow 폴더에 있음
    if (iconValue.startsWith('popow/')) {
      const hasExtension = iconValue.includes('.');
      const imagePath = hasExtension
        ? `/assets/logos/${iconValue}`
        : `/assets/logos/${iconValue}.svg`;

      return (
        <Image
          src={imagePath}
          alt={alt || name}
          width={width}
          height={height}
          className={className}
          style={style}
        />
      );
    }

    // social 로고는 logos/social 폴더에 있음
    if (iconValue.startsWith('social/')) {
      const hasExtension = iconValue.includes('.');
      const imagePath = hasExtension
        ? `/assets/logos/${iconValue}`
        : `/assets/logos/${iconValue}.svg`;

      return (
        <Image
          src={imagePath}
          alt={alt || name}
          width={width}
          height={height}
          className={className}
          style={style}
        />
      );
    }

    // 특정 파일들은 images 폴더에 있음
    const imageFiles = [
      'loading-black.gif',
      'lamp-icon.png',
      'tv-proposal.png',
      'ConnectTiktok.png',
      'mission-dot.gif',
    ];
    if (imageFiles.includes(iconValue)) {
      const hasExtension = iconValue.includes('.');
      const imagePath = hasExtension
        ? `/assets/images/${iconValue}`
        : `/assets/images/${iconValue}.svg`;

      return (
        <Image
          src={imagePath}
          alt={alt || name}
          width={width}
          height={height}
          className={className}
          style={style}
        />
      );
    }

    // legacy/to-delete 폴더의 파일들 (삭제 예정)
    const legacyToDeleteFiles = ['new-reward-policy', 'heart-empty-state', 'starter-mission'];
    if (legacyToDeleteFiles.includes(iconValue)) {
      const hasExtension = iconValue.includes('.');
      const imagePath = hasExtension
        ? `/assets/legacy/to-delete/${iconValue}`
        : `/assets/legacy/to-delete/${iconValue}.svg`;

      return (
        <Image
          src={imagePath}
          alt={alt || name}
          width={width}
          height={height}
          className={className}
          style={style}
        />
      );
    }

    // color 폴더의 컬러 아이콘들
    const colorFiles = [
      'calendar-check',
      'gift',
      'warning-filled',
      'ineligible',
      'inbox',
      'payment-completed',
    ];
    if (colorFiles.includes(iconValue)) {
      const hasExtension = iconValue.includes('.');
      const imagePath = hasExtension
        ? `/assets/icons/color/${iconValue}`
        : `/assets/icons/color/${iconValue}.svg`;

      return (
        <Image
          src={imagePath}
          alt={alt || name}
          width={width}
          height={height}
          className={className}
          style={style}
        />
      );
    }

    // 나머지는 icons 폴더에서 로드
    const hasExtension = iconValue.includes('.');
    const imagePath = hasExtension
      ? `/assets/icons/${iconValue}`
      : `/assets/icons/${iconValue}.svg`;

    const handleError = () => {
      console.warn(`Failed to load icon: ${imagePath}`);
    };

    return (
      <Image
        src={imagePath}
        alt={alt || name}
        width={width}
        height={height}
        className={className}
        style={style}
        onError={handleError}
      />
    );
  }

  // React 컴포넌트인 경우 기존 방식
  const IconComponent = iconValue as React.ComponentType<SVGProps<SVGSVGElement>>;
  return <IconComponent className={className} style={style} {...restProps} />;
};

export default Icon;
